import type { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import type { RuntimeConfig } from "./config";

/**
 * Build react-oidc-context settings for the Cognito user pool. We pass
 * identity_provider=Google so the hosted UI redirects straight to Google
 * instead of showing Cognito's own login form.
 */
export function buildOidcConfig(config: RuntimeConfig): AuthProviderProps {
  const redirect = `${window.location.origin}/`;
  return {
    authority: `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`,
    client_id: config.userPoolClientId,
    redirect_uri: redirect,
    post_logout_redirect_uri: redirect,
    response_type: "code",
    scope: "openid email profile",
    extraQueryParams: { identity_provider: config.identityProvider },
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Strip ?code=&state= from the URL after the redirect completes.
    onSigninCallback: () =>
      window.history.replaceState({}, document.title, window.location.pathname),
  };
}

/**
 * Cognito's OIDC discovery omits end_session_endpoint, so sign-out is a manual
 * redirect to the hosted-UI /logout endpoint after clearing the local session.
 */
export function cognitoLogout(config: RuntimeConfig): void {
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params.toString()}`);
}
